import React, { useEffect, useRef, useCallback, useMemo } from "react";
import { Play, Pause, FastForward, Star, Move } from 'lucide-react';
import useVideoPlayback from "../hooks/useVideoPlayback";
import useTimelineDrag from "../hooks/useTimelineDrag";
import "./VideoPlayer.css";

const VideoPlayer = ({
  videoFile,
  videoRef,
  highlights,
  textOverlays,
  onDeleteHighlight,
  onSelectHighlight,
  onUpdateHighlight,
  onDeleteTextOverlay,
  onSelectTextOverlay,
  onUpdateTextOverlay,
  selectedHighlight,
  selectedTextOverlay,
  activeHighlight,
  onHighlightClick,
  onOutputHighlightClick,
  onMouseDown,
  onAddHighlight,
  onAddTextOverlay
}) => {
  const mainTimelineRef = useRef(null);
  const highlightsTimelineRef = useRef(null);
  const videoContainerRef = useRef(null);
  const zoomRectRef = useRef(null);

  // Use custom hooks
  const {
    isPlaying,
    currentTime,
    setCurrentTime,
    duration,
    playbackRate,
    togglePlay,
    cyclePlaybackRate,
    formatTime,
  } = useVideoPlayback(videoRef, activeHighlight, false);

  const {
    isDragging,
    setIsDragging,
    isTimelineDragging,
    setDraggedItem,
    handleTimelineDragStart,
    handleHighlightDragStart,
    handleZoomRectDragStart,
  } = useTimelineDrag({
    videoRef,
    mainTimelineRef,
    highlightsTimelineRef,
    videoContainerRef,
    zoomRectRef,
    duration,
    selectedHighlight,
    onUpdateHighlight,
    onUpdateTextOverlay,
    onMouseDown,
    setCurrentTime,
  });

  // Sorted highlights for the output timeline
  const sortedHighlights = useMemo(() =>
    [...highlights].sort((a, b) => a.startTime - b.startTime),
    [highlights]
  );

  // Update zoom rectangle position when the selected highlight changes
  useEffect(() => {
    if (!zoomRectRef.current || !videoContainerRef.current || !selectedHighlight?.zoom?.enabled) return;

    const rect = videoContainerRef.current.getBoundingClientRect();
    const rectWidth = rect.width / 2;
    const rectHeight = rect.height / 2;

    // Convert the -100 to 100 range to actual pixel positions
    const x = selectedHighlight.zoom.x || 0;
    const y = selectedHighlight.zoom.y || 0;

    // Map from -100,100 range to 0,containerWidth-rectWidth range
    const leftPos = ((x / 100 + 1) / 2) * (rect.width - rectWidth);
    const topPos = ((y / 100 + 1) / 2) * (rect.height - rectHeight);

    zoomRectRef.current.style.left = `${leftPos}px`;
    zoomRectRef.current.style.top = `${topPos}px`;
    zoomRectRef.current.style.width = `${rectWidth}px`;
    zoomRectRef.current.style.height = `${rectHeight}px`;
  }, [selectedHighlight]);

  const handleMainTimelineClick = useCallback((e) => {
    if (!mainTimelineRef.current || isTimelineDragging || isDragging) return;

    const rect = mainTimelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  }, [isTimelineDragging, isDragging, duration, videoRef, setCurrentTime]);

  const handleHighlightsTimelineClick = useCallback((e) => {
    if (!highlightsTimelineRef.current || isDragging) return;

    // Only seek when directly clicking the timeline, not when clicking a highlight
    if (e.target === highlightsTimelineRef.current) {
      const rect = highlightsTimelineRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = x / rect.width;
      const newTime = percentage * duration;

      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  }, [isDragging, duration, videoRef, setCurrentTime]);

  // Source timeline: select only
  const handleHighlightItemClick = useCallback((e, highlight) => {
    e.stopPropagation();
    onHighlightClick(highlight);
  }, [onHighlightClick]);

  // Output timeline: select + activate (green/loop)
  const handleOutputItemClick = useCallback((e, highlight) => {
    e.stopPropagation();
    onOutputHighlightClick(highlight);
  }, [onOutputHighlightClick]);

  const handleHighlightMouseDown = useCallback((e, highlight) => {
    handleHighlightDragStart(e, highlight);
  }, [handleHighlightDragStart]);

  const handleHighlightTouchStart = useCallback((e, highlight) => {
    e.stopPropagation();
    handleHighlightDragStart(e, highlight);
  }, [handleHighlightDragStart]);

  const handleTextOverlayClick = useCallback((e, textOverlay) => {
    e.stopPropagation();
    onSelectTextOverlay(textOverlay);
  }, [onSelectTextOverlay]);

  const handleTextOverlayMouseDown = useCallback((e, textOverlay) => {
    e.stopPropagation();
    setIsDragging(true);
    setDraggedItem({ type: 'text', item: textOverlay });
    onSelectTextOverlay(textOverlay);
  }, [setIsDragging, setDraggedItem, onSelectTextOverlay]);

  const handleTextOverlayTouchStart = useCallback((e, textOverlay) => {
    e.stopPropagation();
    setIsDragging(true);
    setDraggedItem({ type: 'text', item: textOverlay });
    onSelectTextOverlay(textOverlay);
  }, [setIsDragging, setDraggedItem, onSelectTextOverlay]);

  return (
    <div className="video-player-container">
      <div className="video-wrapper" ref={videoContainerRef}>
        <video
          ref={videoRef}
          src={videoFile}
          className="video"
          controls={false}
          playsInline
          webkit-playsinline="true"
          x5-playsinline="true"
          x-webkit-airplay="allow"
          disablePictureInPicture
          controlsList="nodownload nofullscreen noremoteplayback"
          playbackRate={playbackRate}
          onContextMenu={(e) => e.preventDefault()}
        />

        {/* Zoom rectangle overlay */}
        {selectedHighlight && selectedHighlight.zoom && selectedHighlight.zoom.enabled && (
          <div
            ref={zoomRectRef}
            className="zoom-rectangle"
            onMouseDown={handleZoomRectDragStart}
            onTouchStart={handleZoomRectDragStart}
          >
            <div className="zoom-rectangle-handle">
              <Move size={20} color="#FF4901" />
            </div>
          </div>
        )}
      </div>

      {/* Main timeline for video progression */}
      <div
        ref={mainTimelineRef}
        className="timeline"
        onClick={handleMainTimelineClick}
      >
        <div
          className="timeline-progress"
          style={{ width: `${(currentTime / duration) * 100}%` }}
        />
        <div
          className={`timeline-marker ${isTimelineDragging ? 'dragging' : ''}`}
          style={{ left: `${(currentTime / duration) * 100}%` }}
          onMouseDown={handleTimelineDragStart}
          onTouchStart={handleTimelineDragStart}
        />
      </div>

      {/* Source highlights timeline - for positioning/dragging */}
      <div
        ref={highlightsTimelineRef}
        className="highlights-timeline"
        onClick={handleHighlightsTimelineClick}
      >
        {highlights.map((highlight) => {
          const minWidth = 10;
          const highlightWidth = Math.max(
            (highlight.duration / duration) * 100,
            (minWidth / (highlightsTimelineRef.current?.clientWidth || 1)) * 100
          );

          return (
            <div
              key={highlight.id}
              className={`timeline-highlight ${
                selectedHighlight?.id === highlight.id ? 'selected' : ''
              } ${highlight.slowMotion ? 'slow-motion' : ''} ${highlight.zoom?.enabled ? 'zoomed' : ''}`}
              style={{
                left: `${(highlight.startTime / duration) * 100}%`,
                width: `${highlightWidth}%`,
              }}
              onClick={(e) => handleHighlightItemClick(e, highlight)}
              onMouseDown={(e) => handleHighlightMouseDown(e, highlight)}
              onTouchStart={(e) => handleHighlightTouchStart(e, highlight)}
            />
          );
        })}

        {textOverlays.map((textOverlay) => (
          <div
            key={textOverlay.id}
            className={`timeline-text ${selectedTextOverlay?.id === textOverlay.id ? 'selected' : ''}`}
            style={{
              left: `${(textOverlay.startTime / duration) * 100}%`,
              width: `${(textOverlay.duration / duration) * 100}%`,
            }}
            onClick={(e) => handleTextOverlayClick(e, textOverlay)}
            onMouseDown={(e) => handleTextOverlayMouseDown(e, textOverlay)}
            onTouchStart={(e) => handleTextOverlayTouchStart(e, textOverlay)}
          >
            <div className="timeline-text-label">{textOverlay.text}</div>
          </div>
        ))}
      </div>

      {/* Output timeline - shows highlight render order, click to preview/loop */}
      {sortedHighlights.length > 0 && (
        <div className="output-timeline">
          {sortedHighlights.map((highlight) => (
            <div
              key={highlight.id}
              className={`output-highlight ${activeHighlight?.id === highlight.id ? 'active' : ''} ${
                selectedHighlight?.id === highlight.id ? 'selected' : ''
              } ${highlight.slowMotion ? 'slow-motion' : ''}`}
              onClick={(e) => handleOutputItemClick(e, highlight)}
            />
          ))}
        </div>
      )}

      <div className="video-controls">
        <div className="left-controls">
          <button onClick={togglePlay} className="control-btn">
            {isPlaying ? <Pause size={24} /> : <Play size={24} />}
          </button>

          <button
            onClick={cyclePlaybackRate}
            className="control-btn speed-btn"
            title="Playback Speed"
          >
            <FastForward size={24} />
            <span className="speed-label">{playbackRate}x</span>
          </button>
        </div>

        <div className="right-controls">
          <button
            onClick={onAddHighlight}
            className="control-btn highlight-add-btn"
            title="Add Highlight"
          >
            <Star size={30} />
          </button>
        </div>
        <div className="time-display">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
